import type { PlanNode } from '@timelog/pm-shared';
import { MAX_PLAN_DEPTH } from '@timelog/pm-shared';

import type { FlattenResult } from './flatten';
import type { InsertPosition } from './treeOps';
import { subtreeHeight } from './treeOps';

/**
 * Translates a drop gap in the VISIBLE row list (gapIndex = between rows
 * gapIndex-1 and gapIndex) plus the cursor-derived depth into a tree
 * position. Pure and unit-tested — the pointer hook only does geometry.
 *
 * BR-2 is satisfied by CONVERSION, not refusal: a node landing at root becomes
 * a deliverable and a node landing under a parent becomes a task (`moveSubtree`
 * does it). So nothing here reads `kind` — the drop decides what the node is,
 * never the other way round.
 *
 * It used to read it, and that was the ADR 0028 bug for the third time. The
 * Planning UI has no Deliverable concept — a deliverable and a parent task are
 * the same thing on screen — but the document still stores root nodes as
 * `kind: 'deliverable'`, so gating the depth on kind meant every row of a NEW
 * project (all roots, all deliverables) refused to nest, while Project Ship's
 * rows-inside-a-deliverable nested fine. `kind` is where a node sits, and that
 * is the drop's business.
 */
export type DropResolution = InsertPosition & { depth: number };

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

/** Ancestor of `node` at exactly `depth`, walking the parent index. */
const ancestorAtDepth = (
  flat: FlattenResult,
  nodeId: string,
  fromDepth: number,
  depth: number,
): string | null => {
  let currentId: string | null = nodeId;
  let currentDepth = fromDepth;
  while (currentId !== null && currentDepth > depth) {
    currentId = flat.index.parentById.get(currentId) ?? null;
    currentDepth -= 1;
  }
  return currentId;
};

const childIndexAfter = (
  plan: PlanNode[],
  flat: FlattenResult,
  anchorId: string,
  dragId: string,
): InsertPosition => {
  const parentId = flat.index.parentById.get(anchorId) ?? null;
  const siblings = parentId === null ? plan : (flat.index.nodeById.get(parentId)?.children ?? []);
  let index = siblings.findIndex((sibling) => sibling.id === anchorId) + 1;
  // moveSubtree inserts AFTER removal: a dragged preceding sibling shifts us.
  const dragIndex = siblings.findIndex((sibling) => sibling.id === dragId);
  if (dragIndex !== -1 && dragIndex < index) index -= 1;
  return { parentId, childIndex: index };
};

export type DropDepthRange = { min: number; max: number };

/**
 * The depths a subtree `dragHeight` levels tall may legally land at in this gap.
 * `resolveDropTarget` clamps into exactly this range; the timeline's drag steps
 * through it with the arrow keys, which is how a drop can outdent to an
 * ancestor's level — something the drop zones alone cannot express.
 *
 * Necessary but not sufficient: the deliverable branch can still fail on a
 * missing root anchor, so a non-null range does not guarantee a resolution.
 * Reads only `flat.rows`, so it is O(1) and safe to call per pointer move.
 */
export const dropDepthRange = (
  flat: FlattenResult,
  gapIndex: number,
  dragHeight: number,
): DropDepthRange | null => {
  const previous = flat.rows[gapIndex - 1];
  const next = flat.rows[gapIndex];

  // Nothing above the gap: the only position that exists is the first root.
  if (!previous) return { min: 0, max: 0 };

  // BR-23: the dragged subtree must fit under the ceiling at the offered depth.
  const max = Math.min(previous.depth + 1, MAX_PLAN_DEPTH - 1 - dragHeight);
  // Never shallower than the row below, or the insertion point would fall
  // inside a subtree that visually continues past it. Root (0) is a legal
  // floor now — dragging a subtask out to top level is the gesture it enables.
  const min = Math.max(0, next?.depth ?? 0);
  return min > max ? null : { min, max };
};

export const resolveDropTarget = (
  plan: PlanNode[],
  flat: FlattenResult,
  dragId: string,
  gapIndex: number,
  rawDepth: number,
  /**
   * `subtreeHeight(dragNode)`, when the caller already has it. A drag computes
   * it once on pointerdown; recomputing per pointer move would walk the whole
   * dragged subtree on every frame.
   */
  dragHeight?: number,
): DropResolution | null => {
  const rows = flat.rows;
  const dragNode = flat.index.nodeById.get(dragId);
  if (!dragNode) return null;

  const previous = rows[gapIndex - 1];
  const range = dropDepthRange(flat, gapIndex, dragHeight ?? subtreeHeight(dragNode));
  if (!range) return null;

  // The first gap in the list: the drop becomes the first root.
  if (!previous) return { parentId: null, childIndex: 0, depth: 0 };
  const depth = clamp(rawDepth, range.min, range.max);

  if (depth === previous.depth + 1) {
    // First child of the row above the gap.
    return { parentId: previous.id, childIndex: 0, depth };
  }

  const anchor = ancestorAtDepth(flat, previous.id, previous.depth, depth);
  if (!anchor) return null;
  const position = childIndexAfter(plan, flat, anchor, dragId);
  return { ...position, depth };
};
